import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { marked } from 'marked'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { DropdownMenu } from 'sutra/components'
import { TextArea } from 'sutra/components/Input/TextArea'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Select } from 'sutra/components/Select/Select'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { handleException } from 'clients/exception-handling'
import YogiDivider from 'components/ui/YogiDivider'
import {
  CheckOutlined,
  ContentCopyOutlined,
  IosShareRounded,
  ThumbDownOutlined,
  ThumbUpOutlined,
} from 'features/ask-yogi/components/icons'
import type { ConversationExportFormat } from 'features/ask-yogi/features/conversation/hooks/use-conversation-export'
import { FeedbackData } from 'features/ask-yogi/features/conversation/hooks/use-feedback-mutation'
import { flattenVerbatimBlocks } from 'features/ask-yogi/utils/message-processing'
import { TooltipIconButton } from 'src/components/TooltipIconButton'
import {
  truncateStringFieldsForMixpanel,
  truncateStringFieldToBytes,
} from 'src/features/project/utils'
import mixpanel from 'trackers/mixpanel'

dayjs.extend(utc)

export type Props = {
  message: {
    content: string
    uuid?: string
  }
  index: number
  projectId: string
  onSubmitFeedback: (data: FeedbackData) => Promise<unknown>
  conversationUuid?: string
  createTime?: string
  onExportConversation?: (format: ConversationExportFormat) => void
}

type FeedbackType = 'positive' | 'negative'

const MessageActions: React.FC<Props> = ({
  message,
  index,
  onSubmitFeedback,
  conversationUuid,
  createTime,
  onExportConversation,
}) => {
  const [messageCopyState, setMessageCopyState] = useState<{
    [key: number]: boolean
  }>({})
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null)
  const [issueType, setIssueType] = useState<string | undefined>(undefined)
  const [additionalDetails, setAdditionalDetails] = useState<string>('')
  const [exportMenuOpen, setExportMenuOpen] = useState(false)

  const issueTypeOptions = [
    'The answer was incorrect or irrelevant',
    'The answer was vague or incomplete',
    "The chart didn't match the question",
    "The response was too slow or didn't load",
    'An error prevented me from completing the request',
    'Other (please specify)',
  ]

  const convertMarkdownToText = (markdown: string): string => {
    // Convert markdown to plain text by removing markdown syntax
    let text = markdown
      // Remove chart tags and their contents
      .replace(/<chart\b[^>]*>[\s\S]*?<\/chart>/g, '')
      // Remove citation tags but keep the content clean
      .replace(/<citation>[^<]*<\/citation>/g, '')

    // Handle custom <table> tags (may contain pipe tables or space-separated content)
    // Extract and convert to TSV format
    text = text.replace(
      /<table[^>]*>([\s\S]*?)<\/table>/g,
      (_match, tableContent) => {
        // Split into lines and filter out empty lines
        const tableLines = tableContent
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0)

        // Convert each line: handle both pipe tables and space-separated
        const tsvLines = tableLines
          .map((line: string) => {
            // Check if this is a pipe table row
            if (line.startsWith('|')) {
              // Skip separator rows (|---|---|)
              // Must contain at least 3 dashes and only contain |, -, :, and whitespace
              const isSeparator =
                /^\|[\s:-]+\|$/.test(line) &&
                (line.match(/-/g) || []).length >= 3
              if (isSeparator) {
                return null
              }
              // Convert pipe table row to TSV
              const cells = line
                .split('|')
                .slice(1, -1) // Remove first and last empty elements
                .map((cell) => cell.trim())
              return cells.join('\t')
            } else {
              // Handle space-separated content
              const cells = line.split(/\s{2,}/).map((cell) => cell.trim())
              return cells.join('\t')
            }
          })
          .filter((line: string | null) => line !== null) // Remove null entries (separator rows)

        return '\n' + tsvLines.join('\n') + '\n'
      },
    )

    // Convert markdown tables (with pipes) to tab-separated values (TSV)
    // This makes tables paste nicely into Excel, Google Sheets, etc.
    const lines = text.split('\n')
    const processedLines: string[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!
      // Check if this is a table row (starts with |)
      if (line.trim().startsWith('|')) {
        // Skip separator rows (|---|---|)
        // Must contain at least 3 dashes and only contain |, -, :, and whitespace
        const trimmedLine = line.trim()
        const isSeparator =
          /^\|[\s:-]+\|$/.test(trimmedLine) &&
          (trimmedLine.match(/-/g) || []).length >= 3
        if (isSeparator) {
          continue
        }

        // Convert table row to TSV format
        const cells = line
          .split('|')
          .slice(1, -1) // Remove first and last empty elements
          .map((cell) => cell.trim())

        processedLines.push(cells.join('\t'))
      } else {
        processedLines.push(line)
      }
    }

    text = processedLines.join('\n')

    return (
      text
        // Remove headers
        .replace(/^#{1,6}\s+/gm, '')
        // Remove bold/italic
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        // Remove links
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // Remove inline code
        .replace(/`([^`]+)`/g, '$1')
        // Remove code blocks
        .replace(/```[\s\S]*?```/g, '')
        // Remove blockquotes
        .replace(/^>\s+/gm, '')
        // Remove horizontal rules
        .replace(/^---+$/gm, '')
        // Remove list markers
        .replace(/^[\s]*[-*+]\s+/gm, '')
        .replace(/^[\s]*\d+\.\s+/gm, '')
        // Clean up extra whitespace
        .replace(/\n\s*\n/g, '\n\n')
        .trim()
    )
  }

  const convertMarkdownToHtml = (markdown: string): string => {
    // Remove chart tags and their contents before processing
    let cleanedMarkdown = markdown
      .replace(/<chart\b[^>]*>[\s\S]*?<\/chart>/g, '')
      .replace(/<citation>[^<]*<\/citation>/g, '')

    // Remove custom <table> tags but keep the markdown table content inside
    // This allows the marked library to convert the markdown tables to proper HTML tables
    cleanedMarkdown = cleanedMarkdown.replace(
      /<table[^>]*>([\s\S]*?)<\/table>/g,
      (_match, tableContent) => {
        return '\n' + tableContent + '\n'
      },
    )

    // Convert markdown to HTML using marked
    return marked(cleanedMarkdown) as string
  }

  const handleFeedbackClick = (type: FeedbackType): void => {
    setFeedbackType(type)
    setFeedbackModalOpen(true)
    setIssueType('')
    setAdditionalDetails('')
    const mixpanelValueByType = {
      positive: 'thumbs_up',
      negative: 'thumbs_down',
    }
    mixpanel.track('askyogi', {
      action: 'user_satisfaction',
      value: mixpanelValueByType[type],
      message_uuid: message.uuid,
      conversation_uuid: conversationUuid,
    })
  }

  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)

  const handleFeedbackSubmit = async (): Promise<void> => {
    if (!message.uuid || !conversationUuid) {
      toast.error(
        'Unable to submit feedback: missing message or conversation ID',
      )
      return
    }

    const feedbackData: FeedbackData = {
      message_uuid: message.uuid,
      conversation_uuid: conversationUuid,
      is_positive: feedbackType === 'positive',
      issue_reason: feedbackType === 'negative' ? issueType : null,
      additional_details: additionalDetails.trim() || null,
    }

    setIsSubmittingFeedback(true)
    try {
      await onSubmitFeedback(feedbackData)
      toast.success('Thank you for your feedback!')
      handleFeedbackCancel()
    } catch (cause) {
      handleException(cause, {
        operation: 'conversation.feedback.submit',
        disposition: 'delegated',
        owner: 'MutationCache',
      })

      toast.error('Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmittingFeedback(false)
    }

    mixpanel.track('askyogi', {
      action: 'feedback_given',
      ...truncateStringFieldsForMixpanel(feedbackData),
    })
  }

  const handleFeedbackCancel = (): void => {
    setFeedbackModalOpen(false)
    setFeedbackType(null)
    setIssueType('')
    setAdditionalDetails('')
  }

  const handleCopyMessage = async (): Promise<void> => {
    const tempCopyState = { ...messageCopyState }

    try {
      // Flatten each verbatim card down to a bare `> "<quote>"` line first —
      // the screen renders a card built from the citation API, not the
      // hand-written metadata line old conversations carry; copied text
      // must not claim metadata the screen didn't show.
      const flattenedContent = flattenVerbatimBlocks(message.content)
      const plainText = convertMarkdownToText(flattenedContent)
      const htmlContent = convertMarkdownToHtml(flattenedContent)

      // Create clipboard items with both HTML and plain text
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      })

      await navigator.clipboard.write([clipboardItem])

      tempCopyState[index] = true
      setMessageCopyState(tempCopyState)
      setTimeout(() => {
        const resetState = { ...tempCopyState }
        resetState[index] = false
        setMessageCopyState(resetState)
      }, 2000)
    } catch (_error) {
      handleException(_error, { operation: 'conversation.message.copy' })

      toast.error('Failed to copy message')
    }

    mixpanel.track('askyogi', {
      action: 'response_copied',
      message_uuid: message.uuid,
      conversation_uuid: conversationUuid,
      message_content: truncateStringFieldToBytes(message.content),
    })
  }

  const time = createTime ? dayjs.utc(createTime).local() : null
  const isToday = time?.isSame(dayjs(), 'day')
  const label = time && (isToday ? time.format('h:mm A') : time.format('MMM D'))
  const fullLabel = time?.format('MMM D, YYYY, h:mm A')

  return (
    <>
      <Container>
        {time && (
          <Tooltip content={fullLabel}>
            <Timestamp>{label}</Timestamp>
          </Tooltip>
        )}
        <TooltipIconButton
          tooltipTitle="Copy"
          ariaLabel="Copy message"
          icon={
            messageCopyState[index] ? (
              <CheckOutlined />
            ) : (
              <ContentCopyOutlined />
            )
          }
          onClick={handleCopyMessage}
          height={24}
          width={24}
          variant="muted"
        />
        <TooltipIconButton
          tooltipTitle="Helpful"
          ariaLabel="Mark response helpful"
          icon={<ThumbUpOutlined />}
          onClick={() => handleFeedbackClick('positive')}
          height={24}
          width={24}
          variant="muted"
        />
        <TooltipIconButton
          tooltipTitle="Not helpful"
          ariaLabel="Mark response not helpful"
          icon={<ThumbDownOutlined />}
          onClick={() => handleFeedbackClick('negative')}
          height={24}
          width={24}
          variant="muted"
        />
        {onExportConversation && (
          <DropdownMenu
            open={exportMenuOpen}
            onOpenChange={setExportMenuOpen}
            side="bottom"
            align="end"
            items={[
              {
                key: 'export-as',
                type: 'group',
                label: 'Export as…',
                children: [
                  {
                    key: 'text',
                    label: 'Text',
                    onClick: () => onExportConversation('text'),
                  },
                  {
                    key: 'pdf',
                    label: 'PDF',
                    onClick: () => onExportConversation('pdf'),
                  },
                ],
              },
            ]}
          >
            <TooltipIconButton
              tooltipTitle="Export entire convo"
              ariaLabel="Export entire convo"
              icon={<IosShareRounded />}
              height={24}
              width={24}
              variant="muted"
              active={exportMenuOpen}
            />
          </DropdownMenu>
        )}
      </Container>

      <PopoverModal
        title={'Response Feedback'}
        isOpen={feedbackModalOpen}
        onOk={handleFeedbackSubmit}
        onClose={handleFeedbackCancel}
        okText="Submit"
        cancelText="Cancel"
        okButtonProps={{
          disabled: feedbackType === 'negative' && !issueType,
          loading: isSubmittingFeedback,
        }}
        width={600}
        content={
          <>
            <YogiDivider
              style={{
                width: 'calc(100% + 40px)',
                marginLeft: -20,
                marginBottom: 16,
              }}
            ></YogiDivider>

            {feedbackType === 'negative' && (
              <div style={{ marginBottom: '16px' }}>
                <Label>What type of issue would you like to report? *</Label>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select an issue type"
                  value={issueType || undefined}
                  onChange={(value) => setIssueType(value)}
                  size="md"
                  options={issueTypeOptions.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                />
              </div>
            )}

            <div>
              <Label>Please provide additional details (optional)</Label>
              <TextArea
                rows={6}
                placeholder="Share any additional context or details..."
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
              />
            </div>
          </>
        }
      />
    </>
  )
}

export default MessageActions

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  justify-content: flex-start;
  color: #7f8695;
  margin-top: 4px;
  padding-bottom: 20px;
`

const Timestamp = styled.span`
  font-size: 13px;
  color: #7f8695;
  margin-right: 4px;
`

// We probably want to pull these to the top of the app, but it might break a lot of design stuff when we do it, so just building the new design system here

const Label = styled.div`
  color: #28364f;
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-sm);
  font-style: normal;
  font-weight: 600;
  line-height: var(--Line-height-text-sm, 20px); /* 142.857% */
  margin-bottom: 6px;
`
